import React, { useState, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Table,
  Collapse,
  Badge,
  Row,
  Col,
  ProgressBar,
} from "react-bootstrap";
import { usePost } from "../../api";
import { IMerchantSimple } from "../../interfaces";

interface MerchantUploadModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

interface UploadOptions {
  createAsNew: boolean;
  deleteExisting: boolean;
  preserveHealthy: boolean;
}

interface BulkUploadResponse {
  created: number;
  updated: number;
  preserved: number;
  errors: Array<{ merchant: string; error: string }>;
}

export const MerchantUploadModal: React.FC<MerchantUploadModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const [uploadedMerchants, setUploadedMerchants] = useState<IMerchantSimple[]>(
    []
  );
  const [selectedMerchants, setSelectedMerchants] = useState<Set<string>>(
    new Set()
  );
  const [options, setOptions] = useState<UploadOptions>({
    createAsNew: false,
    deleteExisting: false,
    preserveHealthy: false,
  });
  const [showMerchantList, setShowMerchantList] = useState(false);
  const [error, setError] = useState<string>("");
  const [uploadResult, setUploadResult] = useState<BulkUploadResponse | null>(
    null
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkUpload = usePost<BulkUploadResponse, any>();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError("");
    setUploadResult(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target?.result as string);

        if (!Array.isArray(jsonData)) {
          setError("Invalid JSON format. Expected an array of merchants.");
          return;
        }

        // Validate that each item has required fields
        const validMerchants = jsonData.filter(
          (merchant) =>
            merchant.name && merchant.homePageUrl && merchant.countryCode
        );

        if (validMerchants.length === 0) {
          setError(
            "No valid merchants found. Each merchant must have name, homePageUrl, and countryCode."
          );
          return;
        }

        setUploadedMerchants(validMerchants);
        // Select all merchants by default
        const allMerchantIds = new Set(validMerchants.map((m) => m._id));
        setSelectedMerchants(allMerchantIds);
        setShowMerchantList(true);

        if (validMerchants.length < jsonData.length) {
          setError(
            `${
              jsonData.length - validMerchants.length
            } merchants were skipped due to missing required fields.`
          );
        }
      } catch (err) {
        setError("Invalid JSON file. Please check the format and try again.");
      }
    };

    reader.readAsText(file);
  };

  const getMerchantId = (merchant: IMerchantSimple) => merchant._id;

  const toggleMerchantSelection = (merchantId: string) => {
    const newSelection = new Set(selectedMerchants);
    if (newSelection.has(merchantId)) {
      newSelection.delete(merchantId);
    } else {
      newSelection.add(merchantId);
    }
    setSelectedMerchants(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedMerchants.size === uploadedMerchants.length) {
      setSelectedMerchants(new Set());
    } else {
      const allMerchantIds = new Set(uploadedMerchants.map(getMerchantId));
      setSelectedMerchants(allMerchantIds);
    }
  };

  const handleUpload = async () => {
    if (selectedMerchants.size === 0) {
      setError("Please select at least one merchant to upload.");
      return;
    }

    try {
      setError("");
      setUploadResult(null);

      // Filter merchants based on selection
      const merchantsToUpload = uploadedMerchants.filter((merchant) =>
        selectedMerchants.has(getMerchantId(merchant))
      );

      const result = await bulkUpload.execute("/merchants/bulk-upload", {
        merchants: merchantsToUpload,
        options,
      });

      setUploadResult(result);

      if (result.errors.length === 0) {
        // If no errors, close modal and refresh
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.message || "Upload failed. Please try again.");
    }
  };

  const handleClose = () => {
    setUploadedMerchants([]);
    setSelectedMerchants(new Set());
    setOptions({
      createAsNew: false,
      deleteExisting: false,
      preserveHealthy: false,
    });
    setShowMerchantList(false);
    setError("");
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onHide();
  };

  const getSelectedCount = () => selectedMerchants.size;
  const getTotalCount = () => uploadedMerchants.length;

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Upload Merchants from JSON</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {!uploadResult && (
          <>
            {/* File Upload Section */}
            <Form.Group className="mb-3">
              <Form.Label>Select JSON File</Form.Label>
              <Form.Control
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                ref={fileInputRef}
              />
              <Form.Text className="text-muted">
                Upload a JSON file containing an array of merchant data exported
                from another environment.
              </Form.Text>
            </Form.Group>

            {/* Options Section */}
            {uploadedMerchants.length > 0 && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Check
                        type="switch"
                        id="create-as-new-switch"
                        label="Create as New"
                        checked={options.createAsNew}
                        onChange={(e) =>
                          setOptions((prev) => ({
                            ...prev,
                            createAsNew: e.target.checked,
                          }))
                        }
                      />
                      <Form.Text className="text-muted">
                        Replace existing merchants instead of merging data
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Check
                        type="switch"
                        id="delete-existing-switch"
                        label="Delete Existing"
                        checked={options.deleteExisting}
                        onChange={(e) =>
                          setOptions((prev) => ({
                            ...prev,
                            deleteExisting: e.target.checked,
                          }))
                        }
                      />
                      <Form.Text className="text-danger">
                        Reset environment by deleting existing merchants first
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Check
                        type="switch"
                        id="preserve-healthy-switch"
                        label="Preserve Healthy Merchants"
                        checked={options.preserveHealthy}
                        onChange={(e) =>
                          setOptions((prev) => ({
                            ...prev,
                            preserveHealthy: e.target.checked,
                          }))
                        }
                      />
                      <Form.Text className="text-muted">
                        Do not overwrite merchants with HEALTHY status
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Merchant Selection Summary */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <strong>
                      {getSelectedCount()} of {getTotalCount()} merchants
                      selected
                    </strong>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowMerchantList(!showMerchantList)}
                  >
                    {showMerchantList ? "Hide" : "Show"} Merchant List
                    <i
                      className={`bi bi-chevron-${
                        showMerchantList ? "up" : "down"
                      } ms-1`}
                    ></i>
                  </Button>
                </div>

                {/* Collapsible Merchant List */}
                <Collapse in={showMerchantList}>
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Check
                        type="checkbox"
                        id="select-all-checkbox"
                        label="Select All"
                        checked={
                          selectedMerchants.size === uploadedMerchants.length
                        }
                        onChange={toggleSelectAll}
                      />
                      <Badge bg="secondary">
                        {getSelectedCount()} selected
                      </Badge>
                    </div>

                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}>Select</th>
                            <th>Name</th>
                            <th>Domain</th>
                            <th>Country</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadedMerchants.map((merchant, index) => {
                            const merchantId = getMerchantId(merchant);
                            return (
                              <tr key={index}>
                                <td>
                                  <Form.Check
                                    type="checkbox"
                                    checked={selectedMerchants.has(merchantId)}
                                    onChange={() =>
                                      toggleMerchantSelection(merchantId)
                                    }
                                  />
                                </td>
                                <td>{merchant.name}</td>
                                <td>{merchant.homePageUrl}</td>
                                <td>{merchant.countryCode}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Collapse>
              </>
            )}
          </>
        )}

        {/* Loading State */}
        {bulkUpload.loading && (
          <div className="text-center my-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Uploading...</span>
            </div>
            <div className="mt-2">Uploading merchants...</div>
            <ProgressBar animated now={100} className="mt-2" />
          </div>
        )}

        {/* Upload Results */}
        {uploadResult && (
          <Alert variant="success">
            <Alert.Heading>Upload Complete!</Alert.Heading>
            <p>
              <strong>Created:</strong> {uploadResult.created} merchants
              <br />
              <strong>Updated:</strong> {uploadResult.updated} merchants
              <br />
              <strong>Preserved:</strong> {uploadResult.preserved} merchants
            </p>
            {uploadResult.errors.length > 0 && (
              <>
                <hr />
                <Alert.Heading>Errors:</Alert.Heading>
                <ul>
                  {uploadResult.errors.map((error, index) => (
                    <li key={index}>
                      <strong>{error.merchant}:</strong> {error.error}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Alert>
        )}

        {/* Error Display */}
        {error && <Alert variant="danger">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {uploadResult ? "Close" : "Cancel"}
        </Button>
        {!uploadResult && uploadedMerchants.length > 0 && (
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={bulkUpload.loading || selectedMerchants.size === 0}
          >
            {bulkUpload.loading
              ? "Uploading..."
              : `Upload ${getSelectedCount()} Merchants`}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
